import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { OwnerWorkspaceMetrics } from 'config/api/assetRopa/assetRopaWorkspace.types';
import type { ActiveRopaReviewMetrics } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type MetricItem = {
  key: string;
  label: string;
  value: number;
};

type Props = {
  includedAssetsMessageKey?: 'included_assets' | 'owner_included_assets';
  metrics?: ActiveRopaReviewMetrics;
  workspaceMetrics?: OwnerWorkspaceMetrics;
};

const AssetRopaMetricsSection: React.FC<Props> = ({ includedAssetsMessageKey = 'included_assets', metrics, workspaceMetrics }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const workspaceMessages = processing_activities_messages.asset_ropa.owner_workspace.metrics;

  const metricItems = useMemo((): MetricItem[] => {
    if (metrics) {
      return [
        {
          key: 'includedAssetsCount',
          label: t(processing_activities_messages.asset_ropa.metrics[includedAssetsMessageKey]),
          value: metrics.includedAssetsCount,
        },
        {
          key: 'awaitingResponseCount',
          label: t(processing_activities_messages.asset_ropa.metrics.awaiting_response),
          value: metrics.awaitingResponseCount,
        },
        { key: 'submittedCount', label: t(processing_activities_messages.asset_ropa.metrics.submitted), value: metrics.submittedCount },
        { key: 'acceptedCount', label: t(processing_activities_messages.asset_ropa.metrics.accepted), value: metrics.acceptedCount },
        {
          key: 'clarificationRequestedCount',
          label: t(processing_activities_messages.asset_ropa.metrics.clarification_requested),
          value: metrics.clarificationRequestedCount,
        },
        {
          key: 'assetsWithWarningsCount',
          label: t(processing_activities_messages.asset_ropa.metrics.assets_with_warnings),
          value: metrics.assetsWithWarningsCount,
        },
      ];
    }

    if (!workspaceMetrics) {
      return [];
    }

    return [
      { key: 'gdprRelevantAssetsCount', label: t(workspaceMessages.gdpr_relevant_assets), value: workspaceMetrics.gdprRelevantAssetsCount },
      {
        key: 'assetsWithoutProcessingActivitiesCount',
        label: t(workspaceMessages.without_processing_activities),
        value: workspaceMetrics.assetsWithoutProcessingActivitiesCount,
      },
      {
        key: 'incompleteProcessingsCount',
        label: t(workspaceMessages.incomplete_processings),
        value: workspaceMetrics.incompleteProcessingsCount,
      },
      { key: 'dpiaIndicationsCount', label: t(workspaceMessages.dpia_indications), value: workspaceMetrics.dpiaIndicationsCount },
      {
        key: 'thirdCountryProcessingCount',
        label: t(workspaceMessages.third_country_processing),
        value: workspaceMetrics.thirdCountryProcessingCount,
      },
      {
        key: 'sensitivePersonalDataCount',
        label: t(workspaceMessages.sensitive_personal_data),
        value: workspaceMetrics.sensitivePersonalDataCount,
      },
    ];
  }, [includedAssetsMessageKey, metrics, t, workspaceMessages, workspaceMetrics]);

  if (metricItems.length === 0) return null;

  return (
    <Box
      aria-label={t(metrics ? processing_activities_messages.asset_ropa.metrics.aria_label : workspaceMessages.aria_label)}
      className={styles.metricsRow}
      component='section'
    >
      {metricItems.map(item => (
        <Box key={item.key} aria-label={`${item.label}: ${item.value}`} className={styles.metricTile} role='group'>
          <Box className={styles.metricCard}>
            <Typography className={styles.metricLabel} color='textSecondary' variant='body2'>
              {item.label}
            </Typography>
            <div className={styles.metricValueLine}>
              <Typography className={styles.metricValue} component='div' variant='h4'>
                {item.value}
              </Typography>
            </div>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default AssetRopaMetricsSection;
